/**
 * @kgc/rental-pricing — tier-price
 *
 * A bérlés tier-választásának és bruttó alapdíjának EGYETLEN forrása (ADR-037 Am.3 `D-2`).
 *
 * Miért itt: a `rental.service.ts` `private calculateTierPrice`-a volt az egyetlen hely,
 * ahol a cap-ágak és a `Q-T053` lefokozás élt — a preview-endpoint (`rental-calc.ts`) és a
 * FE-varázsló (`wizard-store.ts`) saját, egyszerűsített if-láncot futtatott. Mérve
 * (2026-08-21): 27 napnál a preview `210 000`, a checkout `150 000` (**+40%**), éjféles,
 * egész napos bemeneten — tehát nem naptár-, hanem cap-eredetű eltérés.
 *
 * A csomag I/O-mentes és függőség nélküli (`package.json` `dependencies: {}`), ezért a FE is
 * hívhatja — a `rental-core` NestJS-t húz, onnan a FE nem tud importálni.
 *
 * ⚠️ A logika a kiemeléskor **változatlan**. A viselkedés-változást a hívók átállítása hozza,
 * nem ez a fájl — ezért mérhető külön a két lépés.
 */

/**
 * A tier értékkészlete. NEM `enum`, mert a `PricingTier` enum a `@kgc/rental-core`-ban él,
 * és a függőség iránya `rental-core → rental-pricing` ⇒ ide nem importálható vissza.
 * A két készlet azonosságát a `tier-price.spec.ts` szinkron-őre méri, nem a jóindulat.
 */
export const PRICING_TIER = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
} as const;

export type PricingTierValue = (typeof PRICING_TIER)[keyof typeof PRICING_TIER];

export interface TierPriceResult {
  tier: PricingTierValue;
  grossAmount: number;
  tierExplanation: string;
}

/** G-FLOAT-1: HUF kerekítés — a forintnak nincs törtrésze. */
function roundHUF(amount: number): number {
  return Math.round(amount);
}

/**
 * Tier-választás + bruttó alapdíj.
 *
 * @param days     naptári napok (a hívó a `calculateCalendarDays`-ből adja — `D-1`)
 * @param dailyRate   napi díj (Ft)
 * @param weeklyRate  heti díj (Ft) — `0`/hiányzó ⇒ a heti tier NEM alkalmazható
 * @param monthlyRate havi díj (Ft) — `0`/hiányzó ⇒ a havi tier NEM alkalmazható
 */
export function calculateTierPrice(
  days: number,
  dailyRate: number,
  weeklyRate: number,
  monthlyRate: number
): TierPriceResult {
  // Q-T053 (döntés-sor 33., P0 MONEY) — TIER-ALKALMAZHATÓSÁGI GUARD.
  // Egy tier CSAK akkor alkalmazható, ha a hozzá tartozó díj POZITÍV. Daily-only gépnél
  // a DB-ből null érkezik → 0 coalesce; a korábbi guard nélküli `Math.min(..., weeklyRate)`
  // és `Math.min(..., monthlyRate)` cap ilyenkor 0-ra húzta a végösszeget ⇒ ≥6 naptól
  // INGYENES volt a bérlés (dev4-live: 5/5 érintett tétel `item_total = 0`).
  // A hiányzó díjú tier nem 0-t ad, hanem LEFOKOZ a legközelebbi árazott tierre.
  const hasWeekly = weeklyRate > 0;
  const hasMonthly = monthlyRate > 0;

  if (days < 7) {
    const grossAmount = roundHUF(dailyRate * days); // G-FLOAT-1: HUF rounding
    // Cap: if daily total exceeds weekly rate near the boundary, use weekly rate
    // Q-T053: a cap csak árazott heti díjnál él (0 = nincs heti díj, nem "ingyen hét").
    const cappedAmount = hasWeekly && days >= 6 ? Math.min(grossAmount, weeklyRate) : grossAmount;
    return {
      tier: PRICING_TIER.DAILY,
      grossAmount: cappedAmount,
      tierExplanation: `${days} nap × ${dailyRate} Ft/nap = ${cappedAmount} Ft`,
    };
  }

  // 30+ days - monthly tier
  // M-100: Fixed 30-day month is a deliberate simplification for rental pricing.
  // This is standard industry practice for equipment rental, not calendar-month billing.
  // Q-T053: a havi ág a HETI ELÉ került — a lefokozás a legközelebbi ÁRAZOTT tierre megy.
  // 30+ napnál havi díj nélkül, de heti díjjal a heti aritmetika a helyes; a tiszta
  // napidíjas fallback ott TÚLSZÁMLÁZNA (37 nap: 185 000 Ft vs. a helyes 135 000 Ft).
  if (days >= 30 && hasMonthly) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    const monthAmount = roundHUF(monthlyRate * months); // G-FLOAT-1: HUF rounding

    let remainderAmount = 0;
    let remainderExplanation = '';

    // Q-T053: a maradék heti bontása csak árazott heti díjnál — különben napidíjas.
    if (remainingDays >= 7 && hasWeekly) {
      const weeks = Math.floor(remainingDays / 7);
      const extraDays = remainingDays % 7;
      remainderAmount = roundHUF(weeklyRate * weeks) + roundHUF(dailyRate * extraDays); // G-FLOAT-1: HUF rounding
      remainderExplanation = `${weeks} hét × ${weeklyRate} Ft + ${extraDays} nap × ${dailyRate} Ft`;
    } else if (remainingDays > 0) {
      remainderAmount = roundHUF(dailyRate * remainingDays); // G-FLOAT-1: HUF rounding
      remainderExplanation = `${remainingDays} nap × ${dailyRate} Ft`;
    }

    const total = monthAmount + remainderAmount;

    return {
      tier: PRICING_TIER.MONTHLY,
      grossAmount: total,
      tierExplanation: `${months} hónap × ${monthlyRate} Ft${remainderExplanation ? ` + ${remainderExplanation}` : ''} = ${total} Ft`,
    };
  }

  // Weekly tier — 7+ nap árazott heti díjjal. Q-T053: ide fut a 30+ napos eset is,
  // ha nincs havi díj (lefokozás heti szintre), nem tiszta napidíjra.
  if (hasWeekly) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    const weekAmount = roundHUF(weeklyRate * weeks); // G-FLOAT-1: HUF rounding
    const dayAmount = roundHUF(dailyRate * remainingDays); // G-FLOAT-1: HUF rounding
    const total = weekAmount + dayAmount;
    // M-016 FIX: Always cap weekly-tier price against monthly rate to prevent
    // price inversion (e.g. 27 days costing more than 30 days monthly rate).
    // Q-T053: a cap csak árazott havi díjnál él.
    const cappedTotal = hasMonthly ? Math.min(total, monthlyRate) : total;

    return {
      tier: PRICING_TIER.WEEKLY,
      grossAmount: cappedTotal,
      tierExplanation: `${weeks} hét × ${weeklyRate} Ft + ${remainingDays} nap × ${dailyRate} Ft = ${cappedTotal} Ft`,
    };
  }

  // Q-T053 FALLBACK — 7+ nap, de a tartamhoz tartozó tier NINCS beárazva.
  // Tiszta napidíjas árazás; a tier-címke DAILY, mert ténylegesen az a díj alkalmazódik.
  // Az M-016 inverzió-védelem megmarad, de csak árazott havi díj mellett.
  const grossAmount = roundHUF(dailyRate * days); // G-FLOAT-1: HUF rounding
  const cappedAmount = hasMonthly ? Math.min(grossAmount, monthlyRate) : grossAmount;

  return {
    tier: PRICING_TIER.DAILY,
    grossAmount: cappedAmount,
    tierExplanation: `${days} nap × ${dailyRate} Ft/nap = ${cappedAmount} Ft (nincs beárazott ${days >= 30 ? 'havi' : 'heti'} díj)`,
  };
}
